import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import type { ShellLink } from '@/config/navigation';
import { buildShellHref } from '@/lib/legacy-url';
import { cn } from '@/lib/utils';

import { SIDEBAR_ICON_ACTIVE, SIDEBAR_ICON_IDLE, sidebarLinkClasses } from './sidebar-styles';

/*
 * Las clases `menu-texto`, `menu-extra` y `menu-linea` las interpreta el CSS del
 * menú contraído (`index.css`): el texto se oculta sólo en escritorio y con el menú
 * contraído, mientras que en el cajón del móvil se sigue viendo entero.
 */

interface SidebarSectionProps {
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Bloque del menú lateral con su título (Categorías, Soporte, …).
 *
 * Con el menú contraído el título se cambia por una línea de separación: los dos
 * elementos están siempre y decide el CSS.
 */
export function SidebarSection({ title, children, className }: SidebarSectionProps) {
  return (
    <div className={cn('mt-6', className)}>
      <p className="menu-texto px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-white/35">
        {title}
      </p>
      <span aria-hidden="true" className="menu-linea mx-3 mb-2 h-px bg-white/10" />
      {children}
    </div>
  );
}

interface SidebarLinkProps {
  to: string;
  label: string;
  icon: ReactNode;
  /** Sólo activo con coincidencia exacta (para rutas que son prefijo de otras). */
  end?: boolean;
  onNavigate?: () => void;
  className?: string;
  contraido?: boolean;
}

/** Enlace del menú hacia una pantalla de la SPA. */
export function SidebarLink({
  to,
  label,
  icon,
  end = false,
  onNavigate,
  className,
  contraido = false,
}: SidebarLinkProps) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        title={contraido ? label : undefined}
        className={({ isActive }) =>
          cn('py-2.5 text-sm font-medium', sidebarLinkClasses(isActive), className)
        }
      >
        {({ isActive }) => (
          <>
            <span
              aria-hidden="true"
              className={cn('shrink-0', isActive ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_IDLE)}
            >
              {icon}
            </span>
            <span className="menu-texto flex-1 truncate">{label}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}

interface SidebarExternalLinkProps {
  label: string;
  icon: ReactNode;
  href: string;
  contraido?: boolean;
}

/** Enlace del menú que abre el panel anterior o un sitio externo. */
export function SidebarExternalLink({
  label,
  icon,
  href,
  contraido = false,
}: SidebarExternalLinkProps) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        title={contraido ? label : undefined}
        className={cn('py-2 text-sm', sidebarLinkClasses(false))}
      >
        <span aria-hidden="true" className={cn('shrink-0', SIDEBAR_ICON_IDLE)}>
          {icon}
        </span>
        <span className="menu-texto flex-1 truncate">{label}</span>
        <ExternalLink
          aria-hidden="true"
          className="menu-extra size-3 shrink-0 text-white/25 transition-colors group-hover:text-white/60"
        />
      </a>
    </li>
  );
}

interface SidebarShellLinkProps {
  link: ShellLink;
  onNavigate?: () => void;
  iconClassName?: string;
  className?: string;
  contraido?: boolean;
}

/**
 * Enlace del shell (accesos rápidos y soporte).
 *
 * Si el módulo ya está portado se navega dentro de la SPA; en caso contrario se
 * compone la URL del panel anterior (o la del sitio externo) y se abre en otra
 * pestaña.
 */
export function SidebarShellLink({
  link,
  onNavigate,
  iconClassName = 'size-4',
  className = 'py-2 font-normal',
  contraido = false,
}: SidebarShellLinkProps) {
  const Icon = link.icon;

  if (link.to) {
    return (
      <SidebarLink
        to={link.to}
        label={link.label}
        onNavigate={onNavigate}
        icon={<Icon className={iconClassName} />}
        className={className}
        contraido={contraido}
      />
    );
  }

  const href = buildShellHref(link);
  if (!href) return null;

  return (
    <SidebarExternalLink
      label={link.label}
      href={href}
      icon={<Icon className={iconClassName} />}
      contraido={contraido}
    />
  );
}
